/**
 * @file orderDeltaSender.service.ts
 * @description Delta Dispatch Engine: ensures only newly added items are sent to the kitchen.
 * 
 * CORE ARCHITECTURAL RULE:
 * When adding subsequent items to an active table, only unsent items (isNewStaged = true)
 * are committed to the kitchen queue. This completely eliminates duplicate tickets in the KDS.
 */

import { supabase } from '../../../config/supabase.config';
import type { StagedOrderItem } from '../../../types/waiter.types';

export interface SendDeltaPayload {
  venueId: string;
  tableId: string;
  tableNumber: string;
  existingOrderId: string | null;
  items: StagedOrderItem[];
  waiterId: string | null;
  waiterName: string;
  guestCount?: number;
}

export const sendOrderDeltaToKitchen = async (
  payload: SendDeltaPayload
): Promise<{ orderId: string; itemsSentCount: number }> => {
  const newItemsToSend = payload.items.filter(item => item.isNewStaged);
  if (newItemsToSend.length === 0) {
    return { orderId: payload.existingOrderId || '', itemsSentCount: 0 };
  }

  let activeOrderId = payload.existingOrderId;

  // 1. If table has no existing active order, create a new one
  if (!activeOrderId) {
    const calculatedSubtotal = payload.items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    const { data: newOrder, error: orderErr } = await supabase
      .from('orders')
      .insert({
        venue_id: payload.venueId,
        table_id: payload.tableId,
        status: 'preparing',
        priority: 'normal',
        total_amount: calculatedSubtotal,
        type: 'dine-in',
        waiter_id: payload.waiterId,
        confirmed_to_kitchen_at: new Date().toISOString(),
        metadata: {
          table_number: payload.tableNumber,
          waiter_name: payload.waiterName,
          guest_count: payload.guestCount || 2,
          service_mode: 'dine-in',
        },
      })
      .select('id')
      .single();

    if (orderErr) throw orderErr;
    activeOrderId = newOrder.id;
  } else {
    // 2. Existing order: update total amount and move status to 'preparing'
    const newTotal = payload.items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    await supabase
      .from('orders')
      .update({
        total_amount: newTotal,
        status: 'preparing',
        waiter_id: payload.waiterId,
      })
      .eq('id', activeOrderId);
  }

  // Ensure activeOrderId is verified non-null before dispatching delta items
  if (!activeOrderId) {
    throw new Error('Failed to resolve active order ID for table dispatch');
  }

  // 3. Insert ONLY the new delta items into order_items
  const rowsToInsert = newItemsToSend.map(item => ({
    order_id: activeOrderId,
    item_id: item.itemId,
    price: item.price,
    quantity: item.quantity,
    notes: item.notes || null,
    status: 'sent_to_kitchen',
    course: item.course,
    sent_to_kitchen_at: new Date().toISOString(),
    selected_modifiers: item.modifiers || [],
  }));

  const { error: itemsErr } = await supabase
    .from('order_items')
    .insert(rowsToInsert);

  if (itemsErr) throw itemsErr;

  return {
    orderId: activeOrderId,
    itemsSentCount: newItemsToSend.length,
  };
};
